import { useMemo, useState, type ReactElement } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  Boxes,
  Layers,
  Plus,
  Timer,
  Wallet,
  Warehouse,
} from "lucide-react";
import { toast } from "sonner";

import {
  Badge,
  Button,
  DataTable,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  type DataTableColumn,
} from "@bizop/ui-kit";
import { X } from "lucide-react";
import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import {
  MovementTypeBadge,
  movementTypeLabel,
  type StockMovementType,
} from "../../../components/MovementTypeBadge";
import {
  StockAdjustmentForm,
  type StockAdjustmentSubmitValues,
  type StockAdjustmentProductOption,
} from "../../../components/forms/StockAdjustmentForm";
import {
  mockProducts,
  mockProductVariants,
  mockStockMovements,
  mockStockReservations,
  mockWarehouses,
  type MockProduct,
  type MockProductVariant,
  type MockStockMovement,
  type MockStockReservation,
  type MockWarehouse,
} from "../../../lib/inventoryMock";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import { useHasPermission } from "../../../lib/permissions";

export const Route = createFileRoute("/_app/inventory/")({
  component: InventoryPage,
});

/* ─────────────────────────────────────────────────────────────
 * Aggregations
 * ───────────────────────────────────────────────────────────── */

type StockCell = {
  productId: string;
  productVariantId: string | null;
  warehouseId: string;
  onHand: number;
  reserved: number;
  available: number;
  /** Weighted average cost per unit, BDT cents. */
  wacBaseCents: bigint;
  /** Total inventory value at WAC for this cell, BDT cents. */
  stockValueBaseCents: bigint;
  /** Has at least one positive ledger row (so WAC is meaningful). */
  hasPositiveValue: boolean;
};

function aggregateStock(
  movements: ReadonlyArray<MockStockMovement>,
  reservations: ReadonlyArray<MockStockReservation>,
): StockCell[] {
  const cells = new Map<string, StockCell>();
  for (const m of movements) {
    const key = `${m.productId}::${m.productVariantId ?? "_"}::${m.warehouseId}`;
    let cell = cells.get(key);
    if (!cell) {
      cell = {
        productId: m.productId,
        productVariantId: m.productVariantId,
        warehouseId: m.warehouseId,
        onHand: 0,
        reserved: 0,
        available: 0,
        wacBaseCents: 0n,
        stockValueBaseCents: 0n,
        hasPositiveValue: false,
      };
      cells.set(key, cell);
    }
    cell.onHand += m.quantityDelta;
    cell.stockValueBaseCents += m.valueDeltaBaseCents;
    if (m.valueDeltaBaseCents > 0n) cell.hasPositiveValue = true;
  }
  // Apply reservations.
  for (const r of reservations) {
    if (r.status !== "active") continue;
    const key = `${r.productId}::${r.productVariantId ?? "_"}::${r.warehouseId}`;
    const cell = cells.get(key);
    if (!cell) continue;
    cell.reserved += r.quantityReserved;
  }
  // Compute WAC and finalise.
  const out: StockCell[] = [];
  for (const cell of cells.values()) {
    cell.available = cell.onHand - cell.reserved;
    if (cell.onHand > 0 && cell.stockValueBaseCents > 0n) {
      cell.wacBaseCents = cell.stockValueBaseCents / BigInt(cell.onHand);
    } else {
      // No on-hand units → no value attribution.
      cell.wacBaseCents = 0n;
      cell.stockValueBaseCents = 0n;
    }
    out.push(cell);
  }
  return out;
}

/* ─────────────────────────────────────────────────────────────
 * Page component
 * ───────────────────────────────────────────────────────────── */

function InventoryPage(): ReactElement {
  const isMock = useMockData();
  // Cost / WAC / stock-value are finance-sensitive — gated to finance.read.
  const canSeeCost = useHasPermission("finance.read");

  // Source data — for now mock-only. When InstantDB queries are wired,
  // swap these for live queries and merge with the mock fallback when
  // isMock=true.
  void isMock; // keep the hook as a future-proofing flag

  const [movements, setMovements] = usePersistentMockState("stockMovements", mockStockMovements);
  const [reservations] = usePersistentMockState("stockReservations", mockStockReservations);
  const [products] = usePersistentMockState("products", mockProducts);
  const [variants] = usePersistentMockState("productVariants", mockProductVariants);
  const [warehouses] = usePersistentMockState("warehouses", mockWarehouses);

  const productById = useMemo(
    () => new Map(products.map((p) => [p._id, p])),
    [products],
  );
  const variantById = useMemo(
    () => new Map(variants.map((v) => [v._id, v])),
    [variants],
  );
  const warehouseById = useMemo(
    () => new Map(warehouses.map((w) => [w._id, w])),
    [warehouses],
  );

  const allCells = useMemo(
    () => aggregateStock(movements, reservations),
    [movements, reservations],
  );
  const cells = useMemo(
    () => allCells.filter((c) => c.onHand !== 0),
    [allCells],
  );

  /**
   * O(1) lookup keyed by product+variant+warehouse. Used by the stock
   * adjustment form's before/after preview. Returns null when we have
   * no record for that cell — the form treats null as "stock not yet
   * tracked here" and renders zero as the baseline.
   */
  const cellByKey = useMemo(() => {
    const m = new Map<string, StockCell>();
    for (const c of allCells) {
      m.set(`${c.productId}::${c.productVariantId ?? "_"}::${c.warehouseId}`, c);
    }
    return m;
  }, [allCells]);

  const lookupStock = useMemo(
    () =>
      ({
        productId,
        productVariantId,
        warehouseId,
      }: {
        productId: string;
        productVariantId: string | null;
        warehouseId: string;
      }) => {
        const cell = cellByKey.get(
          `${productId}::${productVariantId ?? "_"}::${warehouseId}`,
        );
        return cell ? { onHand: cell.onHand } : { onHand: 0 };
      },
    [cellByKey],
  );

  /* ───── KPIs ───── */

  const totalStockValueBaseCents = useMemo(
    () => cells.reduce((sum, c) => sum + c.stockValueBaseCents, 0n),
    [cells],
  );

  const skuCount = useMemo(
    () =>
      new Set(
        cells.map((c) => `${c.productId}::${c.productVariantId ?? "_"}`),
      ).size,
    [cells],
  );

  // Low-stock heuristic per spec: any (product, warehouse) cell under 10 units.
  const lowStockCount = useMemo(
    () => cells.filter((c) => c.onHand > 0 && c.onHand < 10).length,
    [cells],
  );

  const recentMovementsCount = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return movements.filter((m) => m.occurredAt >= cutoff).length;
  }, [movements]);

  /* ───── Tabs + low-stock filter ───── */

  const [activeTab, setActiveTab] = useState<"byProduct" | "byWarehouse" | "movementLog">("byProduct");
  const [lowStockOnly, setLowStockOnly] = useState(false);

  function handleLowStockKpiClick() {
    if (lowStockCount === 0) return;
    setActiveTab("byProduct");
    setLowStockOnly(true);
  }

  /* ───── Adjust-stock sheet ───── */

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const productOptions = useMemo<StockAdjustmentProductOption[]>(
    () =>
      products.map((p) => ({
        id: p._id,
        sku: p.sku,
        displayName: p.displayName,
        variants: variants
          .filter((v) => v.productId === p._id)
          .map((v) => ({
            id: v._id,
            variantSku: v.variantSku,
            variantName: v.variantName,
          })),
      })),
    [products, variants],
  );
  const warehouseOptions = useMemo(
    () =>
      warehouses
        .filter((w) => w.isActive)
        .map((w) => ({
          id: w._id,
          code: w.code,
          displayName: w.displayName,
        })),
    [warehouses],
  );

  async function handleAdjustSubmit(values: StockAdjustmentSubmitValues) {
    setSubmitting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 250));

      // Compute the running balance for this product+variant+warehouse cell
      const cellKey = `${values.productId}::${values.productVariantId ?? "_"}::${values.warehouseId}`;
      const existingCell = cellByKey.get(cellKey);
      const previousOnHand = existingCell?.onHand ?? 0;
      const balanceAfter = previousOnHand + values.quantitySigned;

      const unitCostCents = values.costPerUnitCents ?? 0n;
      const valueDelta = unitCostCents * BigInt(Math.abs(values.quantitySigned)) *
        BigInt(values.quantitySigned >= 0 ? 1 : -1);

      const newMovement: MockStockMovement = {
        _id: `sm_adj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        productId: values.productId,
        productVariantId: values.productVariantId,
        warehouseId: values.warehouseId,
        movementType: "adjustment",
        quantityDelta: values.quantitySigned,
        quantityBalanceAfter: balanceAfter,
        unitCostBaseCents: unitCostCents,
        valueDeltaBaseCents: valueDelta,
        referenceType: "manual_adjustment",
        referenceId: null,
        occurredAt: Date.now(),
        notes: values.reason,
      };

      setMovements((prev) => [...prev, newMovement]);

      const product = productById.get(values.productId);
      const warehouse = warehouseById.get(values.warehouseId);
      toast.success("Adjustment posted", {
        description: `${
          values.quantitySigned > 0 ? "+" : ""
        }${values.quantitySigned} ${product?.sku ?? "SKU"} @ ${warehouse?.code ?? "WH"} — ${values.reason}`,
      });
      setAdjustOpen(false);
    } finally {
      setSubmitting(false);
    }
  }

  /* ───── Row drilldown ───── */

  const [drilldown, setDrilldown] = useState<StockCell | null>(null);

  const drilldownMovements = useMemo(() => {
    if (!drilldown) return [];
    return movements
      .filter(
        (m) =>
          m.productId === drilldown.productId &&
          (m.productVariantId ?? null) === drilldown.productVariantId &&
          m.warehouseId === drilldown.warehouseId,
      )
      .slice()
      .sort((a, b) => b.occurredAt - a.occurredAt);
  }, [drilldown, movements]);

  /* ───── Export ───── */

  function handleExportCsv(extension: "csv" | "xls") {
    if (activeTab === "movementLog") {
      type MovRow = {
        occurredAt: string;
        sku: string;
        productName: string;
        variantSku: string;
        warehouseCode: string;
        warehouseName: string;
        movementType: string;
        quantityDelta: number;
        quantityBalanceAfter: number;
        unitCostBaseCents: bigint;
        valueDeltaBaseCents: bigint;
        referenceType: string;
        referenceId: string;
        notes: string;
      };
      const rows: MovRow[] = movements.map((m) => {
        const product = productById.get(m.productId);
        const variant = m.productVariantId ? variantById.get(m.productVariantId) : null;
        const warehouse = warehouseById.get(m.warehouseId);
        return {
          occurredAt: formatDateForCsv(m.occurredAt),
          sku: product?.sku ?? "",
          productName: product?.displayName ?? "",
          variantSku: variant?.variantSku ?? "",
          warehouseCode: warehouse?.code ?? "",
          warehouseName: warehouse?.displayName ?? "",
          movementType: movementTypeLabel(m.movementType),
          quantityDelta: m.quantityDelta,
          quantityBalanceAfter: m.quantityBalanceAfter,
          unitCostBaseCents: canSeeCost ? m.unitCostBaseCents : 0n,
          valueDeltaBaseCents: canSeeCost ? m.valueDeltaBaseCents : 0n,
          referenceType: m.referenceType ?? "",
          referenceId: m.referenceId ?? "",
          notes: m.notes ?? "",
        };
      });
      const cols: ExportColumn<MovRow>[] = [
        { key: "occurredAt", label: "Date" },
        { key: "sku", label: "SKU" },
        { key: "productName", label: "Product" },
        { key: "variantSku", label: "Variant" },
        { key: "warehouseCode", label: "Warehouse code" },
        { key: "warehouseName", label: "Warehouse" },
        { key: "movementType", label: "Type" },
        { key: "quantityDelta", label: "Qty Δ" },
        { key: "quantityBalanceAfter", label: "Balance after" },
        ...(canSeeCost
          ? ([
              { key: "unitCostBaseCents", label: "Unit cost BDT" },
              { key: "valueDeltaBaseCents", label: "Value Δ BDT" },
            ] as ExportColumn<MovRow>[])
          : []),
        { key: "referenceType", label: "Ref type" },
        { key: "referenceId", label: "Ref id" },
        { key: "notes", label: "Notes" },
      ];
      downloadCsv(
        exportFilename("inventory-movements", extension),
        toCsv(rows, cols),
      );
      return;
    }
    if (activeTab === "byWarehouse") {
      type WhRow = {
        warehouseCode: string;
        warehouseName: string;
        sku: string;
        productName: string;
        variantSku: string;
        onHand: number;
        reserved: number;
        available: number;
        wacBaseCents: bigint;
        stockValueBaseCents: bigint;
      };
      const rows: WhRow[] = cells.map((c) => {
        const wh = warehouseById.get(c.warehouseId);
        const p = productById.get(c.productId);
        const v = c.productVariantId ? variantById.get(c.productVariantId) : null;
        return {
          warehouseCode: wh?.code ?? "",
          warehouseName: wh?.displayName ?? "",
          sku: p?.sku ?? "",
          productName: p?.displayName ?? "",
          variantSku: v?.variantSku ?? "",
          onHand: c.onHand,
          reserved: c.reserved,
          available: c.available,
          wacBaseCents: canSeeCost ? c.wacBaseCents : 0n,
          stockValueBaseCents: canSeeCost ? c.stockValueBaseCents : 0n,
        };
      });
      const cols: ExportColumn<WhRow>[] = [
        { key: "warehouseCode", label: "Warehouse code" },
        { key: "warehouseName", label: "Warehouse" },
        { key: "sku", label: "SKU" },
        { key: "productName", label: "Product" },
        { key: "variantSku", label: "Variant" },
        { key: "onHand", label: "On hand" },
        { key: "reserved", label: "Reserved" },
        { key: "available", label: "Available" },
        ...(canSeeCost
          ? ([
              { key: "wacBaseCents", label: "WAC BDT" },
              { key: "stockValueBaseCents", label: "Stock value BDT" },
            ] as ExportColumn<WhRow>[])
          : []),
      ];
      downloadCsv(
        exportFilename("inventory-by-warehouse", extension),
        toCsv(rows, cols),
      );
      return;
    }
    // byProduct (default)
    type ProdRow = {
      sku: string;
      productName: string;
      variantSku: string;
      warehouseCode: string;
      warehouseName: string;
      onHand: number;
      reserved: number;
      available: number;
      wacBaseCents: bigint;
      stockValueBaseCents: bigint;
    };
    const rows: ProdRow[] = cells.map((c) => {
      const p = productById.get(c.productId);
      const v = c.productVariantId ? variantById.get(c.productVariantId) : null;
      const wh = warehouseById.get(c.warehouseId);
      return {
        sku: p?.sku ?? "",
        productName: p?.displayName ?? "",
        variantSku: v?.variantSku ?? "",
        warehouseCode: wh?.code ?? "",
        warehouseName: wh?.displayName ?? "",
        onHand: c.onHand,
        reserved: c.reserved,
        available: c.available,
        wacBaseCents: canSeeCost ? c.wacBaseCents : 0n,
        stockValueBaseCents: canSeeCost ? c.stockValueBaseCents : 0n,
      };
    });
    const cols: ExportColumn<ProdRow>[] = [
      { key: "sku", label: "SKU" },
      { key: "productName", label: "Product" },
      { key: "variantSku", label: "Variant" },
      { key: "warehouseCode", label: "Warehouse code" },
      { key: "warehouseName", label: "Warehouse" },
      { key: "onHand", label: "On hand" },
      { key: "reserved", label: "Reserved" },
      { key: "available", label: "Available" },
      ...(canSeeCost
        ? ([
            { key: "wacBaseCents", label: "WAC BDT" },
            { key: "stockValueBaseCents", label: "Stock value BDT" },
          ] as ExportColumn<ProdRow>[])
        : []),
    ];
    downloadCsv(
      exportFilename("inventory-by-product", extension),
      toCsv(rows, cols),
    );
  }

  /* ───── Render ───── */

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Inventory"
        description="Stock ledger across raw material, finished goods, and dealer consignment warehouses."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <Button onClick={() => setAdjustOpen(true)}>
              <Plus className="size-4" />
              Adjust stock
            </Button>
          </>
        }
      />

      <KpiStrip
        totalStockValueBaseCents={totalStockValueBaseCents}
        skuCount={skuCount}
        lowStockCount={lowStockCount}
        recentMovementsCount={recentMovementsCount}
        canSeeCost={canSeeCost}
        onLowStockClick={handleLowStockKpiClick}
      />

      <Tabs
        value={activeTab}
        onValueChange={(v) =>
          setActiveTab(v as "byProduct" | "byWarehouse" | "movementLog")
        }
      >
        <TabsList>
          <TabsTrigger value="byProduct">By Product</TabsTrigger>
          <TabsTrigger value="byWarehouse">By Warehouse</TabsTrigger>
          <TabsTrigger value="movementLog">Movement Log</TabsTrigger>
        </TabsList>

        <TabsContent value="byProduct" className="mt-4">
          {lowStockOnly ? (
            <div className="mb-3 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setLowStockOnly(false)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full",
                  "border border-(--danger)/30 bg-(--danger-soft)",
                  "px-2.5 py-1 text-[12px] text-(--danger)",
                  "hover:bg-(--danger-soft)/80",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-(--border-focus)",
                )}
                aria-label="Clear low-stock filter"
              >
                <AlertTriangle className="h-3 w-3" aria-hidden />
                Low stock only
                <X className="h-3 w-3" aria-hidden />
              </button>
              <span className="text-[11px] text-(--text-tertiary) tabular-nums">
                {cells.filter((c) => c.onHand > 0 && c.onHand < 10).length}{" "}
                cells under reorder threshold
              </span>
            </div>
          ) : null}
          <ByProductTable
            cells={cells}
            productById={productById}
            variantById={variantById}
            warehouseById={warehouseById}
            canSeeCost={canSeeCost}
            onRowClick={setDrilldown}
            lowStockOnly={lowStockOnly}
          />
        </TabsContent>

        <TabsContent value="byWarehouse" className="mt-4">
          <ByWarehouseTable
            cells={cells}
            warehouseById={warehouseById}
            canSeeCost={canSeeCost}
          />
        </TabsContent>

        <TabsContent value="movementLog" className="mt-4">
          <MovementLog
            movements={movements}
            productById={productById}
            variantById={variantById}
            warehouseById={warehouseById}
            canSeeCost={canSeeCost}
          />
        </TabsContent>
      </Tabs>

      {/* Adjust-stock sheet */}
      <Sheet open={adjustOpen} onOpenChange={setAdjustOpen}>
        <SheetContent size="md">
          <SheetHeader>
            <SheetTitle>Adjust stock</SheetTitle>
            <SheetDescription>
              Manual movements post immediately to the ledger and are
              recorded in the audit log with the reason you provide.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <StockAdjustmentForm
              products={productOptions}
              warehouses={warehouseOptions}
              lookupStock={lookupStock}
              onSubmit={handleAdjustSubmit}
              onCancel={() => setAdjustOpen(false)}
              submitting={submitting}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Per-cell drilldown sheet */}
      <Sheet
        open={drilldown !== null}
        onOpenChange={(open) => {
          if (!open) setDrilldown(null);
        }}
      >
        <SheetContent size="lg">
          <SheetHeader>
            <SheetTitle>
              {drilldown
                ? productById.get(drilldown.productId)?.displayName ?? "Stock"
                : "Stock"}
            </SheetTitle>
            <SheetDescription>
              {drilldown ? (
                <>
                  Movement history at{" "}
                  <span className="font-medium text-(--text-primary)">
                    {warehouseById.get(drilldown.warehouseId)?.displayName}
                  </span>
                  .
                </>
              ) : null}
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            {drilldown ? (
              <DrilldownPanel
                cell={drilldown}
                movements={drilldownMovements}
                canSeeCost={canSeeCost}
              />
            ) : null}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * KPI strip
 * ───────────────────────────────────────────────────────────── */

function KpiStrip({
  totalStockValueBaseCents,
  skuCount,
  lowStockCount,
  recentMovementsCount,
  canSeeCost,
  onLowStockClick,
}: {
  totalStockValueBaseCents: bigint;
  skuCount: number;
  lowStockCount: number;
  recentMovementsCount: number;
  canSeeCost: boolean;
  onLowStockClick: () => void;
}) {
  const lowStockClickable = lowStockCount > 0;
  return (
    <section
      className={cn(
        "grid grid-cols-1 sm:grid-cols-2 gap-4",
        canSeeCost ? "lg:grid-cols-4" : "lg:grid-cols-3",
      )}
      aria-label="Inventory KPIs"
    >
      {canSeeCost ? (
        <KpiCard
          caption={
            <span className="inline-flex items-center gap-1.5">
              <Wallet className="size-3" />
              Total stock value
            </span>
          }
          value={formatMoneySmart(totalStockValueBaseCents, "BDT", { locale: "en-BD" })}
          hint="At weighted-average cost"
        />
      ) : null}
      <KpiCard
        caption={
          <span className="inline-flex items-center gap-1.5">
            <Boxes className="size-3" />
            SKUs in stock
          </span>
        }
        value={new Intl.NumberFormat("en-BD").format(skuCount)}
        hint="Distinct product+variant cells with positive on-hand"
      />
      {lowStockClickable ? (
        <button
          type="button"
          onClick={onLowStockClick}
          className={cn(
            "text-left rounded-(--radius-md) transition-colors",
            "bg-(--danger-soft) border border-(--danger)/30",
            "hover:bg-(--danger-soft)/80",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-(--border-focus)",
          )}
          aria-label={`Filter to ${lowStockCount} low-stock cells`}
        >
          <KpiCard
            caption={
              <span className="inline-flex items-center gap-1.5 text-(--danger)">
                <AlertTriangle className="size-3" />
                Low-stock alerts
              </span>
            }
            value={
              <span className="text-(--danger)">
                {new Intl.NumberFormat("en-BD").format(lowStockCount)}
              </span>
            }
            hint="Click to filter By Product"
            className="bg-transparent border-0 shadow-none hover:bg-transparent"
          />
        </button>
      ) : (
        <KpiCard
          caption={
            <span className="inline-flex items-center gap-1.5">
              <AlertTriangle className="size-3" />
              Low-stock alerts
            </span>
          }
          value={new Intl.NumberFormat("en-BD").format(lowStockCount)}
          hint="Cells with fewer than 10 units"
        />
      )}
      <KpiCard
        caption={
          <span className="inline-flex items-center gap-1.5">
            <Timer className="size-3" />
            Recent movements
          </span>
        }
        value={new Intl.NumberFormat("en-BD").format(recentMovementsCount)}
        hint="Posted in the last 24 hours"
      />
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Tab: By product
 * ───────────────────────────────────────────────────────────── */

type ByProductRow = StockCell & {
  sku: string;
  productName: string;
  variantName: string | null;
  warehouseName: string;
};

function ByProductTable({
  cells,
  productById,
  variantById,
  warehouseById,
  canSeeCost,
  onRowClick,
  lowStockOnly = false,
}: {
  cells: StockCell[];
  productById: Map<string, MockProduct>;
  variantById: Map<string, MockProductVariant>;
  warehouseById: Map<string, MockWarehouse>;
  canSeeCost: boolean;
  onRowClick: (cell: StockCell) => void;
  lowStockOnly?: boolean;
}) {
  const rows = useMemo<ByProductRow[]>(() => {
    const filtered = lowStockOnly
      ? cells.filter((c) => c.onHand > 0 && c.onHand < 10)
      : cells;
    return filtered
      .map((c) => {
        const product = productById.get(c.productId);
        const variant = c.productVariantId
          ? variantById.get(c.productVariantId)
          : null;
        const warehouse = warehouseById.get(c.warehouseId);
        return {
          ...c,
          sku: variant?.variantSku ?? product?.sku ?? "—",
          productName: product?.displayName ?? "—",
          variantName: variant?.variantName ?? null,
          warehouseName: warehouse?.displayName ?? "—",
        };
      })
      .sort((a, b) =>
        a.stockValueBaseCents > b.stockValueBaseCents
          ? -1
          : a.stockValueBaseCents < b.stockValueBaseCents
            ? 1
            : 0,
      );
  }, [cells, productById, variantById, warehouseById, lowStockOnly]);

  const columns = useMemo<DataTableColumn<ByProductRow>[]>(
    () => {
      const cols: DataTableColumn<ByProductRow>[] = [
        {
          id: "sku",
          accessorKey: "sku",
          header: "SKU",
          meta: { mono: true },
          cell: (info) => (
            <span className="font-mono text-(--text-primary)">
              {info.getValue() as string}
            </span>
          ),
        },
        {
          id: "product",
          header: "Product",
          cell: (info) => (
            <div className="flex flex-col">
              <span className="text-(--text-primary)">
                {info.row.original.productName}
              </span>
              {info.row.original.variantName ? (
                <span className="text-[11px] text-(--text-tertiary)">
                  {info.row.original.variantName}
                </span>
              ) : null}
            </div>
          ),
        },
        {
          id: "warehouse",
          header: "Warehouse",
          cell: (info) => (
            <span className="text-(--text-secondary)">
              {info.row.original.warehouseName}
            </span>
          ),
        },
        {
          id: "onHand",
          header: "On-hand",
          meta: { numeric: true },
          cell: (info) => (
            <span className="text-(--text-primary)">
              {info.row.original.onHand.toLocaleString("en-BD")}
            </span>
          ),
        },
        {
          id: "reserved",
          header: "Reserved",
          meta: { numeric: true },
          cell: (info) => {
            const v = info.row.original.reserved;
            return (
              <span
                className={cn(
                  v > 0 ? "text-(--warning)" : "text-(--text-tertiary)",
                )}
              >
                {v.toLocaleString("en-BD")}
              </span>
            );
          },
        },
        {
          id: "available",
          header: "Available",
          meta: { numeric: true },
          cell: (info) => {
            const v = info.row.original.available;
            return (
              <span
                className={cn(
                  v <= 0
                    ? "text-(--danger)"
                    : v < 10
                      ? "text-(--warning)"
                      : "text-(--text-primary)",
                )}
              >
                {v.toLocaleString("en-BD")}
              </span>
            );
          },
        },
      ];

      if (canSeeCost) {
        cols.push(
          {
            id: "wac",
            header: "WAC",
            meta: { numeric: true },
            cell: (info) => (
              <span className="text-(--text-secondary)">
                {formatMoney(info.row.original.wacBaseCents, "BDT", {
                  locale: "en-BD",
                })}
              </span>
            ),
          },
          {
            id: "value",
            header: "Stock value",
            meta: { numeric: true },
            cell: (info) => (
              <span className="text-(--text-primary)">
                {formatMoney(info.row.original.stockValueBaseCents, "BDT", {
                  locale: "en-BD",
                })}
              </span>
            ),
          },
        );
      }

      return cols;
    },
    [canSeeCost],
  );

  return (
    <DataTable
      data={rows}
      columns={columns}
      stickyFirstColumn
      emptyTitle={lowStockOnly ? "No low-stock cells" : "No stock yet"}
      emptyDescription={
        lowStockOnly
          ? "All product+warehouse cells are above the reorder threshold."
          : "Once movements are posted, the live cell view appears here."
      }
      emptyIcon={<Layers className="size-5 text-(--text-tertiary)" />}
      onRowClick={(row) => onRowClick(row.original)}
    />
  );
}

/* ─────────────────────────────────────────────────────────────
 * Tab: By warehouse
 * ───────────────────────────────────────────────────────────── */

type ByWarehouseRow = {
  warehouseId: string;
  warehouseCode: string;
  warehouseName: string;
  productCount: number;
  totalValueBaseCents: bigint;
};

function ByWarehouseTable({
  cells,
  warehouseById,
  canSeeCost,
}: {
  cells: StockCell[];
  warehouseById: Map<string, MockWarehouse>;
  canSeeCost: boolean;
}) {
  const rows = useMemo<ByWarehouseRow[]>(() => {
    const map = new Map<string, ByWarehouseRow>();
    for (const c of cells) {
      const warehouse = warehouseById.get(c.warehouseId);
      if (!warehouse) continue;
      const existing = map.get(c.warehouseId);
      if (existing) {
        existing.productCount += 1;
        existing.totalValueBaseCents += c.stockValueBaseCents;
      } else {
        map.set(c.warehouseId, {
          warehouseId: c.warehouseId,
          warehouseCode: warehouse.code,
          warehouseName: warehouse.displayName,
          productCount: 1,
          totalValueBaseCents: c.stockValueBaseCents,
        });
      }
    }
    return Array.from(map.values()).sort(
      (a, b) =>
        Number(b.totalValueBaseCents - a.totalValueBaseCents) ||
        a.warehouseName.localeCompare(b.warehouseName),
    );
  }, [cells, warehouseById]);

  const columns = useMemo<DataTableColumn<ByWarehouseRow>[]>(
    () => {
      const cols: DataTableColumn<ByWarehouseRow>[] = [
        {
          id: "code",
          accessorKey: "warehouseCode",
          header: "Code",
          meta: { mono: true },
          cell: (info) => (
            <span className="font-mono text-(--text-primary)">
              {info.getValue() as string}
            </span>
          ),
        },
        {
          id: "name",
          header: "Warehouse",
          cell: (info) => (
            <span className="text-(--text-primary)">
              {info.row.original.warehouseName}
            </span>
          ),
        },
        {
          id: "productCount",
          header: "Products",
          meta: { numeric: true },
          cell: (info) => (
            <span className="text-(--text-primary)">
              {info.row.original.productCount.toLocaleString("en-BD")}
            </span>
          ),
        },
      ];

      if (canSeeCost) {
        cols.push({
          id: "totalValue",
          header: "Total value",
          meta: { numeric: true },
          cell: (info) => (
            <span className="text-(--text-primary)">
              {formatMoney(info.row.original.totalValueBaseCents, "BDT", {
                locale: "en-BD",
              })}
            </span>
          ),
        });
      }

      return cols;
    },
    [canSeeCost],
  );

  return (
    <DataTable
      data={rows}
      columns={columns}
      emptyTitle="No active warehouses"
      emptyIcon={<Warehouse className="size-5 text-(--text-tertiary)" />}
    />
  );
}

/* ─────────────────────────────────────────────────────────────
 * Tab: Movement log
 * ───────────────────────────────────────────────────────────── */

const MOVEMENT_TYPE_FILTERS: Array<{
  value: StockMovementType | "all";
  label: string;
}> = [
  { value: "all", label: "All movement types" },
  { value: "purchase_receipt", label: movementTypeLabel("purchase_receipt") },
  { value: "sales_shipment", label: movementTypeLabel("sales_shipment") },
  {
    value: "manufacturing_consumption",
    label: movementTypeLabel("manufacturing_consumption"),
  },
  {
    value: "manufacturing_output",
    label: movementTypeLabel("manufacturing_output"),
  },
  { value: "transfer_in", label: movementTypeLabel("transfer_in") },
  { value: "transfer_out", label: movementTypeLabel("transfer_out") },
  { value: "adjustment", label: movementTypeLabel("adjustment") },
  { value: "sales_return", label: movementTypeLabel("sales_return") },
  { value: "purchase_return", label: movementTypeLabel("purchase_return") },
];

type MovementLogRow = MockStockMovement & {
  productLabel: string;
  warehouseLabel: string;
};

function MovementLog({
  movements,
  productById,
  variantById,
  warehouseById,
  canSeeCost,
}: {
  movements: ReadonlyArray<MockStockMovement>;
  productById: Map<string, MockProduct>;
  variantById: Map<string, MockProductVariant>;
  warehouseById: Map<string, MockWarehouse>;
  canSeeCost: boolean;
}) {
  const [productFilter, setProductFilter] = useState<string>("all");
  const [warehouseFilter, setWarehouseFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<StockMovementType | "all">(
    "all",
  );
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const filtered = useMemo<MovementLogRow[]>(() => {
    const fromTs = fromDate ? new Date(fromDate).getTime() : null;
    const toTs = toDate ? new Date(toDate).getTime() + 24 * 60 * 60 * 1000 : null;
    return movements
      .filter((m) => {
        if (productFilter !== "all" && m.productId !== productFilter)
          return false;
        if (warehouseFilter !== "all" && m.warehouseId !== warehouseFilter)
          return false;
        if (typeFilter !== "all" && m.movementType !== typeFilter) return false;
        if (fromTs !== null && m.occurredAt < fromTs) return false;
        if (toTs !== null && m.occurredAt > toTs) return false;
        return true;
      })
      .slice()
      .sort((a, b) => b.occurredAt - a.occurredAt)
      .map((m) => {
        const product = productById.get(m.productId);
        const variant = m.productVariantId
          ? variantById.get(m.productVariantId)
          : null;
        const warehouse = warehouseById.get(m.warehouseId);
        return {
          ...m,
          productLabel:
            (variant?.variantSku ?? product?.sku ?? "—") +
            (product ? ` · ${product.displayName}` : ""),
          warehouseLabel: warehouse
            ? `${warehouse.code} · ${warehouse.displayName}`
            : "—",
        };
      });
  }, [
    movements,
    productById,
    variantById,
    warehouseById,
    productFilter,
    warehouseFilter,
    typeFilter,
    fromDate,
    toDate,
  ]);

  const columns = useMemo<DataTableColumn<MovementLogRow>[]>(
    () => {
      const cols: DataTableColumn<MovementLogRow>[] = [
        {
          id: "occurredAt",
          header: "Time",
          cell: (info) => (
            <span className="font-mono text-[12px] text-(--text-secondary) tabular-nums">
              {new Date(info.row.original.occurredAt).toLocaleString("en-BD", {
                dateStyle: "short",
                timeStyle: "short",
              })}
            </span>
          ),
        },
        {
          id: "type",
          header: "Type",
          cell: (info) => (
            <MovementTypeBadge type={info.row.original.movementType} />
          ),
        },
        {
          id: "product",
          header: "Product",
          cell: (info) => (
            <span className="text-(--text-primary)">
              {info.row.original.productLabel}
            </span>
          ),
        },
        {
          id: "warehouse",
          header: "Warehouse",
          cell: (info) => (
            <span className="text-(--text-secondary)">
              {info.row.original.warehouseLabel}
            </span>
          ),
        },
        {
          id: "qty",
          header: "Qty",
          meta: { numeric: true },
          cell: (info) => {
            const v = info.row.original.quantityDelta;
            return (
              <span
                className={cn(
                  "font-mono",
                  v > 0 ? "text-(--success)" : v < 0 ? "text-(--danger)" : "text-(--text-tertiary)",
                )}
              >
                {v > 0 ? "+" : ""}
                {v.toLocaleString("en-BD")}
              </span>
            );
          },
        },
      ];

      if (canSeeCost) {
        cols.push({
          id: "cost",
          header: "Unit cost",
          meta: { numeric: true },
          cell: (info) => (
            <span className="text-(--text-secondary)">
              {formatMoney(info.row.original.unitCostBaseCents, "BDT", {
                locale: "en-BD",
              })}
            </span>
          ),
        });
      }

      cols.push({
        id: "notes",
        header: "Notes",
        cell: (info) => (
          <span className="text-(--text-tertiary) line-clamp-1">
            {info.row.original.notes ?? ""}
          </span>
        ),
      });

      return cols;
    },
    [canSeeCost],
  );

  return (
    <div className="flex flex-col gap-4">
      <div
        className={cn(
          "flex flex-wrap items-end gap-3",
          "rounded-md border border-(--border-subtle) bg-(--bg-surface) p-3",
        )}
      >
        <FilterField label="Product">
          <select
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className={selectClassName}
          >
            <option value="all">All products</option>
            {Array.from(productById.values()).map((p) => (
              <option key={p._id} value={p._id}>
                {p.sku} · {p.displayName}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="Warehouse">
          <select
            value={warehouseFilter}
            onChange={(e) => setWarehouseFilter(e.target.value)}
            className={selectClassName}
          >
            <option value="all">All warehouses</option>
            {Array.from(warehouseById.values()).map((w) => (
              <option key={w._id} value={w._id}>
                {w.code} · {w.displayName}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="Type">
          <select
            value={typeFilter}
            onChange={(e) =>
              setTypeFilter(e.target.value as StockMovementType | "all")
            }
            className={selectClassName}
          >
            {MOVEMENT_TYPE_FILTERS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="From">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className={selectClassName}
          />
        </FilterField>
        <FilterField label="To">
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className={selectClassName}
          />
        </FilterField>
        <div className="ml-auto text-[12px] text-(--text-tertiary) tabular-nums">
          Showing {filtered.length.toLocaleString("en-BD")} movements
        </div>
      </div>

      <DataTable
        data={filtered}
        columns={columns}
        emptyTitle="No movements match"
        emptyDescription="Try clearing or widening the filters."
        emptyIcon={<Layers className="size-5 text-(--text-tertiary)" />}
      />
    </div>
  );
}

const selectClassName = cn(
  "h-8 rounded-md border border-(--border-default) bg-(--bg-surface)",
  "px-2 text-[12px] text-(--text-primary)",
  "focus:outline-none focus:border-(--border-focus)",
);

function FilterField({
  label,
  children,
}: {
  label: string;
  children: ReactElement;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </span>
      {children}
    </label>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Drilldown panel
 * ───────────────────────────────────────────────────────────── */

function DrilldownPanel({
  cell,
  movements,
  canSeeCost,
}: {
  cell: StockCell;
  movements: MockStockMovement[];
  canSeeCost: boolean;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div
        className={cn(
          "grid gap-3",
          canSeeCost ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3",
        )}
      >
        <Stat label="On-hand" value={cell.onHand.toLocaleString("en-BD")} />
        <Stat
          label="Reserved"
          value={cell.reserved.toLocaleString("en-BD")}
          tone={cell.reserved > 0 ? "warning" : "neutral"}
        />
        <Stat
          label="Available"
          value={cell.available.toLocaleString("en-BD")}
          tone={
            cell.available <= 0
              ? "danger"
              : cell.available < 10
                ? "warning"
                : "neutral"
          }
        />
        {canSeeCost ? (
          <Stat
            label="Stock value"
            value={formatMoney(cell.stockValueBaseCents, "BDT", { locale: "en-BD" })}
          />
        ) : null}
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-[13px] font-semibold text-(--text-primary)">
          Movement history
        </h3>
        <Badge variant="neutral" size="sm">
          {movements.length.toLocaleString("en-BD")} entries
        </Badge>
      </div>

      <ul className="flex flex-col gap-2">
        {movements.map((m) => (
          <li
            key={m._id}
            className={cn(
              "rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-3",
              "flex items-start gap-3",
            )}
          >
            <MovementTypeBadge type={m.movementType} />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-3">
                <span
                  className={cn(
                    "font-mono text-[13px] tabular-nums",
                    m.quantityDelta > 0
                      ? "text-(--success)"
                      : m.quantityDelta < 0
                        ? "text-(--danger)"
                        : "text-(--text-tertiary)",
                  )}
                >
                  {m.quantityDelta > 0 ? "+" : ""}
                  {m.quantityDelta.toLocaleString("en-BD")}
                </span>
                <span className="text-[11px] text-(--text-tertiary) tabular-nums">
                  {new Date(m.occurredAt).toLocaleString("en-BD", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </span>
              </div>
              <div className="text-[12px] text-(--text-secondary)">
                {canSeeCost ? (
                  <>
                    Unit cost{" "}
                    {formatMoney(m.unitCostBaseCents, "BDT", { locale: "en-BD" })}
                    {" · "}
                  </>
                ) : null}
                Balance after{" "}
                {m.quantityBalanceAfter.toLocaleString("en-BD")}
              </div>
              {m.notes ? (
                <div className="mt-1 text-[12px] text-(--text-tertiary) italic">
                  {m.notes}
                </div>
              ) : null}
            </div>
          </li>
        ))}
        {movements.length === 0 ? (
          <li className="rounded-md border border-dashed border-(--border-default) bg-(--bg-canvas) p-4 text-center text-[12px] text-(--text-tertiary)">
            No movements recorded for this cell.
          </li>
        ) : null}
      </ul>
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "neutral" | "warning" | "danger";
}) {
  return (
    <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-3">
      <div className="text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div
        className={cn(
          "mt-1 font-mono text-[16px] font-semibold tabular-nums",
          tone === "warning"
            ? "text-(--warning)"
            : tone === "danger"
              ? "text-(--danger)"
              : "text-(--text-primary)",
        )}
      >
        {value}
      </div>
    </div>
  );
}
